import { useEffect, useMemo, useState } from "react";
import { Button, Card, CircularProgress } from "@firecms/ui";
import { httpsCallable } from "firebase/functions";

import { UnlockMode } from "../collections/units";
import type { EventTask } from "../collections/eventTask";
import { AdminModal } from "../custom/AdminModal";
import type {
  EventContentLevelRow,
  EventContentUnitSection,
} from "../eventContent/aggregateEventContentOverview";
import { levelDisplayName } from "../eventContent/levelDisplayName";
import { useEventContentOverview } from "../hooks/useEventContentOverview";
import { useFirebaseFunctions } from "../hooks/useFirebaseFunctions";
import {
  useEventUnitReleases,
  type EventUnitRelease,
} from "../hooks/useEventUnitReleases";
import type { AdminTranslateFn } from "../i18n/getAdminT";
import { useAdminTranslation } from "../i18n/useAdminTranslation";
import type { Locale } from "../localization/constants";
import { copyTextToClipboard } from "../workspace/adminLinks";
import type { EventCollectionId } from "../workspace/collectionIds";
import { UnitUnlockControls } from "./UnitUnlockControls";
import {
  statsMuted,
  statsPageTitle,
  statsProgressTrack,
  statsSectionTitle,
} from "./statsViewTheme";

type EventContentViewProps = {
  eventId: EventCollectionId;
};

function formatAverage(value: number | null): string {
  if (value == null) {
    return "—";
  }
  return value.toFixed(1);
}

function formatDurationSeconds(
  value: number | null,
  sampleCount: number
): string {
  if (value == null || sampleCount <= 0) {
    return "—";
  }
  if (value < 60) {
    return `${value.toFixed(1)}s`;
  }
  const minutes = Math.floor(value / 60);
  const seconds = Math.round(value % 60);
  return `${minutes}m ${seconds}s`;
}

function cooldownRemainingMs(
  compute: EventTask | null,
  nowMs: number
): number {
  if (!compute?.cooldownUntil || compute.status === "running") {
    return 0;
  }
  const remaining = compute.cooldownUntil.getTime() - nowMs;
  return remaining > 0 ? remaining : 0;
}

function StatTile({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className="rounded-lg border border-surface-200 bg-surface-50 px-4 py-3 dark:border-surface-600 dark:bg-surface-800">
      <div className={`text-xs font-medium uppercase tracking-wide ${statsMuted}`}>
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold tabular-nums text-surface-900 dark:text-surface-100">
        {value}
      </div>
      {hint ? <div className={`mt-1 text-xs ${statsMuted}`}>{hint}</div> : null}
    </div>
  );
}

function LevelStatsDialog({
  level,
  participantCount,
  onClose,
  t,
}: {
  level: EventContentLevelRow;
  participantCount: number;
  onClose: () => void;
  t: AdminTranslateFn;
}) {
  const stats = level.stats;
  const starMax = Math.max(1, ...stats.starDistribution);
  const hasStars = stats.starDistribution.some((count) => count > 0);
  const name = levelDisplayName(level, t);

  return (
    <AdminModal
      open
      onClose={onClose}
      ariaLabel={t("eventContent.statsAria")}
      title={t("eventContent.statsTitle")}
      description={`${name} · ${level.levelCode}`}
      maxWidth="2xl"
      footer={
        <Button onClick={onClose} color="primary">
          {t("eventContent.close")}
        </Button>
      }
    >
      <div className="space-y-5">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatTile
            label={t("eventContent.notStarted")}
            value={String(stats.notStarted)}
            hint={t("eventContent.percentOfParticipants", {
              percent: participantCount > 0
                ? Math.round((stats.notStarted / participantCount) * 100)
                : 0,
            })}
          />
          <StatTile
            label={t("eventContent.inProgress")}
            value={String(stats.inProgress)}
            hint={t("eventContent.percentOfParticipants", {
              percent: participantCount > 0
                ? Math.round((stats.inProgress / participantCount) * 100)
                : 0,
            })}
          />
          <StatTile
            label={t("eventContent.completed")}
            value={String(stats.completed)}
            hint={t("eventContent.percentOfParticipants", {
              percent: stats.completedPercent,
            })}
          />
        </div>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatTile
            label={t("eventContent.avgStars")}
            value={formatAverage(stats.avgStars)}
          />
          <StatTile
            label={t("eventContent.avgScore")}
            value={formatAverage(stats.avgScore)}
          />
          <StatTile
            label={t("eventContent.avgDuration")}
            value={formatDurationSeconds(
              stats.avgDurationSeconds,
              stats.durationSampleCount
            )}
          />
        </div>

        {hasStars ? (
          <div>
            <div className={statsSectionTitle}>
              {t("eventContent.starDistribution")}
            </div>
            <ul className="mt-3 space-y-2">
              {stats.starDistribution.map((count, stars) => (
                <li key={stars} className="flex items-center gap-3">
                  <span className={`w-20 shrink-0 text-xs ${statsMuted}`}>
                    {t("eventContent.starsLabel", { stars })}
                  </span>
                  <div className={`flex-1 ${statsProgressTrack}`}>
                    <div
                      className="h-full rounded-full bg-amber-500 dark:bg-amber-400"
                      style={{ width: `${Math.round((count / starMax) * 100)}%` }}
                    />
                  </div>
                  <span className="w-8 shrink-0 text-right text-xs tabular-nums">
                    {count}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
      </div>
    </AdminModal>
  );
}

function CopyAccessCodeButton({
  code,
  t,
}: {
  code: string;
  t: AdminTranslateFn;
}) {
  const [copied, setCopied] = useState(false);

  return (
    <Button
      size="small"
      variant="outlined"
      onClick={async () => {
        const ok = await copyTextToClipboard(code);
        if (!ok) {
          return;
        }
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      }}
    >
      {copied
        ? t("eventContent.copiedAccessCode")
        : t("eventContent.copyAccessCode")}
    </Button>
  );
}

function UnitCard({
  unit,
  expanded,
  eventId,
  release,
  locale,
  onToggle,
  onViewStats,
  t,
}: {
  unit: EventContentUnitSection;
  expanded: boolean;
  eventId: EventCollectionId;
  release: EventUnitRelease | undefined;
  locale: Locale;
  onToggle: () => void;
  onViewStats: (level: EventContentLevelRow) => void;
  t: AdminTranslateFn;
}) {
  const isBlocked = unit.unlockMode === UnlockMode.UNLOCK_MODE_BLOCKED;

  return (
    <Card className="border-surface-200 shadow-sm dark:border-surface-700 dark:bg-surface-800">
      <div className="p-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            className="min-w-0 flex-1 rounded-md text-left"
            aria-expanded={expanded}
            aria-label={
              expanded
                ? t("eventContent.collapseUnit")
                : t("eventContent.expandUnit")
            }
            onClick={onToggle}
          >
            <div className="flex items-start gap-2">
              <span
                className="material-icons mt-0.5 text-[20px] text-surface-500 dark:text-surface-400"
                aria-hidden
              >
                {expanded ? "expand_less" : "expand_more"}
              </span>
              <div className="min-w-0 flex-1">
                <div className={statsSectionTitle}>
                  {unit.unitName}{" "}
                  <span className={`text-sm font-normal ${statsMuted}`}>
                    ({unit.unitCode})
                  </span>
                </div>
              </div>
            </div>
          </button>
          {isBlocked ? (
            <UnitUnlockControls
              eventId={eventId}
              unitId={unit.unitId}
              release={release}
              locale={locale}
              t={t}
            />
          ) : null}
        </div>

        {isBlocked && unit.accessCode ? (
          <div className="mt-2 ml-7 flex flex-wrap items-center gap-2">
            <div className={`text-xs ${statsMuted}`}>
              {t("eventContent.accessCode")}:{" "}
              <span className="font-mono font-semibold tracking-wide text-surface-800 dark:text-surface-200">
                {unit.accessCode}
              </span>
            </div>
            <CopyAccessCodeButton code={unit.accessCode} t={t} />
          </div>
        ) : null}
        {!isBlocked ? (
          <div className={`mt-2 ml-7 text-xs ${statsMuted}`}>
            {t("eventContent.unlockModeFree")}
          </div>
        ) : null}

        {expanded ? (
          unit.levels.length === 0 ? (
            <p className={`mt-3 text-sm ${statsMuted}`}>
              {t("eventContent.noLevels")}
            </p>
          ) : (
            <ul className="mt-4 space-y-2">
              {unit.levels.map((level) => {
                const name = levelDisplayName(level, t);
                return (
                  <li
                    key={level.levelId}
                    className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-surface-200 px-3 py-2 dark:border-surface-600"
                  >
                    <div className="flex min-w-0 flex-1 items-center gap-3">
                      <span
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold tabular-nums text-white"
                        aria-label={t("eventContent.levelNumber", {
                          order: level.order,
                        })}
                      >
                        {level.order}
                      </span>
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">{name}</div>
                        <div className={`truncate font-mono text-xs ${statsMuted}`}>
                          {level.levelCode}
                        </div>
                      </div>
                    </div>
                    <div className="flex flex-wrap items-center gap-3">
                      {unit.statsLoaded ? (
                        <div className={`text-xs tabular-nums ${statsMuted}`}>
                          {t("eventContent.startedCount", {
                            count: level.stats.started,
                            percent: level.stats.startedPercent,
                          })}
                          {" · "}
                          {t("eventContent.completedCount", {
                            count: level.stats.completed,
                            percent: level.stats.completedPercent,
                          })}
                        </div>
                      ) : null}
                      <Button
                        size="small"
                        variant="outlined"
                        disabled={!unit.statsLoaded}
                        onClick={() => onViewStats(level)}
                      >
                        {t("eventContent.viewStats")}
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )
        ) : null}
      </div>
    </Card>
  );
}

export function EventContentView({ eventId }: EventContentViewProps) {
  const { t, locale } = useAdminTranslation();
  const functions = useFirebaseFunctions();
  const { overview, loading, catalogLoaded, unpublished, compute } =
    useEventContentOverview(eventId);
  const releases = useEventUnitReleases(eventId);
  const [selectedLevel, setSelectedLevel] = useState<EventContentLevelRow | null>(
    null
  );
  const [expandedUnitIds, setExpandedUnitIds] = useState<Record<string, true>>(
    {}
  );
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [queueing, setQueueing] = useState(false);
  const [queueError, setQueueError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const empty = catalogLoaded && overview.units.length === 0;
  const running = compute?.status === "running";
  const remainingCooldownMs = cooldownRemainingMs(compute, nowMs);
  const computeDisabled =
    queueing || running || remainingCooldownMs > 0 || unpublished || empty;

  useEffect(() => {
    if (!running && remainingCooldownMs <= 0) {
      return;
    }
    const interval = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [running, remainingCooldownMs]);

  useEffect(() => {
    setExpandedUnitIds({});
    setSelectedLevel(null);
    setConfirmOpen(false);
    setQueueError(null);
  }, [eventId]);

  const statusMessage = useMemo(() => {
    if (running) {
      return `${t("eventContent.computeStatsRunning")} · ${compute?.progress ?? 0}%`;
    }
    if (compute?.status === "failed" && compute.error) {
      return t("eventContent.computeStatsFailed", { error: compute.error });
    }
    if (remainingCooldownMs > 0) {
      return t("eventContent.computeStatsCooldown", {
        seconds: Math.max(1, Math.ceil(remainingCooldownMs / 1000)),
      });
    }
    if (compute?.completedAt) {
      return t("eventContent.computeStatsLastRun", {
        when: compute.completedAt.toLocaleString(locale),
      });
    }
    return t("eventContent.computeStatsNever");
  }, [compute, locale, remainingCooldownMs, running, t]);

  const toggleUnit = (unitId: string) => {
    const isExpanded = expandedUnitIds[unitId] === true;
    if (isExpanded) {
      setExpandedUnitIds((prev) => {
        const next = { ...prev };
        delete next[unitId];
        return next;
      });
      return;
    }
    setExpandedUnitIds((prev) => ({ ...prev, [unitId]: true }));
  };

  const queueCompute = async () => {
    if (!functions) {
      setQueueError("Internal error");
      return;
    }
    setQueueing(true);
    setQueueError(null);
    try {
      const callable = httpsCallable(functions, "computeLevelStats");
      await callable({
        accountId: eventId.accountId,
        eventId: eventId.eventId,
      });
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : t("eventContent.unlockFailed");
      setQueueError(message);
    } finally {
      setQueueing(false);
      setConfirmOpen(false);
    }
  };

  return (
    <div className="flex h-full w-full bg-surface-50 dark:bg-surface-900">
      <div className="mx-auto w-full max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className={statsPageTitle}>{t("eventContent.viewTitle")}</h1>
            <p className={`mt-2 text-sm ${statsMuted}`}>
              {t("eventContent.viewDescription")}
            </p>
          </div>
          {!unpublished && !empty ? (
            <Button
              variant="outlined"
              disabled={computeDisabled}
              onClick={() => setConfirmOpen(true)}
            >
              {queueing || running
                ? `${t("eventContent.computeStatsRunning")} ${compute?.progress ?? 0}%`
                : t("eventContent.computeStats")}
            </Button>
          ) : null}
        </div>

        {!unpublished && !empty ? (
          <p className={`mt-3 text-sm ${statsMuted}`}>
            {statusMessage}
            {queueError ? ` · ${queueError}` : ""}
          </p>
        ) : null}

        {loading ? (
          <div className="mt-6 flex justify-center">
            <CircularProgress size="medium" />
          </div>
        ) : null}

        {!loading && empty ? (
          <p className={`mt-8 text-sm ${statsMuted}`}>
            {t(
              unpublished
                ? "eventContent.emptyUnpublished"
                : "eventContent.empty"
            )}
          </p>
        ) : null}

        {!loading && !empty ? (
          <div className="mt-6 flex flex-col gap-10">
            {overview.units.map((unit) => (
              <UnitCard
                key={unit.unitId}
                unit={unit}
                expanded={expandedUnitIds[unit.unitId] === true}
                eventId={eventId}
                release={releases[unit.unitId]}
                locale={locale}
                onToggle={() => toggleUnit(unit.unitId)}
                onViewStats={setSelectedLevel}
                t={t}
              />
            ))}
          </div>
        ) : null}
      </div>

      {selectedLevel ? (
        <LevelStatsDialog
          level={selectedLevel}
          participantCount={overview.participantCount}
          onClose={() => setSelectedLevel(null)}
          t={t}
        />
      ) : null}

      {confirmOpen ? (
        <AdminModal
          open
          onClose={() => setConfirmOpen(false)}
          ariaLabel={t("eventContent.computeStatsConfirmTitle")}
          title={t("eventContent.computeStatsConfirmTitle")}
          description={t("eventContent.computeStatsConfirmBody")}
          maxWidth="lg"
          footer={
            <>
              <Button variant="text" onClick={() => setConfirmOpen(false)}>
                {t("eventContent.close")}
              </Button>
              <Button
                color="primary"
                disabled={queueing}
                onClick={() => void queueCompute()}
              >
                {t("eventContent.computeStats")}
              </Button>
            </>
          }
        />
      ) : null}
    </div>
  );
}
